import { Link } from "react-router-dom";
import { Leaf, Gauge, Sparkles, ArrowRight, Star } from "lucide-react";
import { Header, Footer } from "@/components/site/Chrome";

export default function Landing() {
  return (
    <>
      <Header />

      <main className="pt-16">
        {/* Hero */}
        <section
          data-testid="hero-section"
          className="relative bg-white overflow-hidden"
        >
          <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-10 pb-8">
            <div className="rounded-3xl overflow-hidden border border-gray-100 shadow-sm mb-8 bg-gray-100">
              <img
                src="/hero.jpg"
                alt="Freshly power-washed clean wooden deck in warm sunlight"
                className="w-full h-56 sm:h-72 object-cover"
                loading="eager"
                fetchPriority="high"
              />
            </div>

            <h1
              data-testid="hero-headline"
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#334E68] leading-[1.05]"
            >
              Bring Your Outdoor Space<br className="hidden sm:block" />{" "}
              Back To Life.
            </h1>

            <p
              data-testid="hero-subtext"
              className="mt-4 text-base sm:text-lg text-gray-600 leading-relaxed"
            >
              Hoboken's 5-Star Rated Professional Service since 2017.
            </p>

            <ul className="mt-6 space-y-3">
              <li className="flex items-start gap-3">
                <span className="mt-0.5 flex items-center justify-center w-7 h-7 rounded-full bg-green-50 text-[#16A34A] shrink-0">
                  <Leaf className="w-4 h-4" />
                </span>
                <span className="text-sm sm:text-base text-gray-700">
                  Eco-friendly products
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-0.5 flex items-center justify-center w-7 h-7 rounded-full bg-green-50 text-[#16A34A] shrink-0">
                  <Gauge className="w-4 h-4" />
                </span>
                <span className="text-sm sm:text-base text-gray-700">
                  1700 PSI washer
                </span>
              </li>
              <li className="flex items-start gap-3">
                <span className="mt-0.5 flex items-center justify-center w-7 h-7 rounded-full bg-green-50 text-[#16A34A] shrink-0">
                  <Sparkles className="w-4 h-4" />
                </span>
                <span className="text-sm sm:text-base text-gray-700">
                  Sidewalks, decks, patios &amp; outdoor furniture
                </span>
              </li>
            </ul>

            <Link
              to="/estimate"
              data-testid="hero-cta-estimate"
              className="mt-8 group w-full min-h-[56px] bg-[#16A34A] hover:bg-[#15803D] active:bg-[#166534] text-white rounded-2xl font-semibold text-base tracking-wide uppercase transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-[#16A34A] focus:ring-offset-2 flex items-center justify-center gap-2"
            >
              Get an Instant Estimate
              <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
            </Link>
          </div>
        </section>

        {/* Trust strip */}
        <section
          data-testid="trust-strip"
          className="bg-[#F9FAFB] border-y border-gray-100"
        >
          <div className="max-w-3xl mx-auto px-4 sm:px-6 py-5">
            <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-sm text-gray-600 font-medium text-center">
              <span data-testid="trust-neighbors">
                Trusted by <span className="text-[#334E68] font-semibold">75+</span> Hoboken neighbors
              </span>
              <span className="text-gray-300">·</span>
              <span data-testid="trust-rate">
                <span className="text-[#334E68] font-semibold">$100/hr</span>
              </span>
              <span className="text-gray-300">·</span>
              <span
                data-testid="trust-stars"
                className="inline-flex items-center gap-0.5 text-[#F59E0B]"
                aria-label="5 star rating"
              >
                {[0, 1, 2, 3, 4].map((i) => (
                  <Star key={i} className="w-4 h-4 fill-current" />
                ))}
              </span>
            </div>
          </div>
        </section>

        {/* Secondary explainer */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 py-12">
          <h2 className="font-display text-2xl sm:text-3xl font-semibold text-[#334E68] tracking-tight">
            Simple. Local. Reliable.
          </h2>
          <p className="mt-3 text-base text-gray-600 leading-relaxed">
            Send a few photos of what needs cleaning, pick a day that works, and
            Charlie will confirm your slot personally. No sales calls, no
            surprise fees.
          </p>

          <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
            {[
              { n: "1", t: "Send photos", d: "Snap 1–4 quick pics of the area." },
              { n: "2", t: "See your estimate", d: "Transparent range, $100/hr." },
              { n: "3", t: "Pick a day", d: "Charlie texts to confirm." },
            ].map((s) => (
              <div
                key={s.n}
                className="bg-white rounded-2xl p-5 border border-gray-100"
              >
                <div className="w-8 h-8 rounded-full bg-[#334E68] text-white flex items-center justify-center text-sm font-semibold">
                  {s.n}
                </div>
                <p className="mt-3 font-display font-semibold text-[#334E68]">
                  {s.t}
                </p>
                <p className="mt-1 text-sm text-gray-600 leading-relaxed">
                  {s.d}
                </p>
              </div>
            ))}
          </div>

          <Link
            to="/estimate"
            data-testid="secondary-cta-estimate"
            className="mt-10 w-full min-h-[56px] bg-[#334E68] hover:bg-[#293e52] text-white rounded-2xl font-semibold text-base tracking-wide uppercase transition-colors flex items-center justify-center gap-2"
          >
            Get an Instant Estimate
            <ArrowRight className="w-5 h-5" />
          </Link>
        </section>
      </main>

      <Footer />
    </>
  );
}
